import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface UsePWAUpdateReturn {
  needRefresh: boolean;
  isUpdating: boolean;
  updateProgress: number; // 0-100
  updateServiceWorker: (reloadPage: boolean) => void;
  dismissUpdate: () => void;
}

/**
 * PWA 앱 업데이트 감지 및 관리 훅
 * - Service Worker 업데이트 감지
 * - 업데이트 진행 상태 추적
 * - 자동 페이지 새로고침
 */
export function usePWAUpdate(): UsePWAUpdateReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [dismissedUpdateTimestamp, setDismissedUpdateTimestamp] = useState<number | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      if (!r) return;

      // (1) 페이지 로드 직후 1회 즉시 update 트리거 — 새 배포를 빠르게 감지.
      // Why: 사용자가 staging 탭을 열어두고 다른 작업하다 돌아왔을 때
      // 옛 SW가 cache-first로 옛 번들을 계속 서빙하는 사고를 막는다.
      r.update().catch((e) => console.error('SW initial update failed:', e));

      // (2) 5분 주기 polling으로 idle 탭에서도 빠르게 새 SW 인지.
      // 1시간 → 5분 단축은 "배포 후 사용자가 새 변경을 보기까지의 horizon" 단축이 목표.
      setInterval(async () => {
        try {
          await r.update();
        } catch (e) {
          console.error('SW update check failed:', e);
        }
      }, 5 * 60 * 1000);

      // (3) 탭이 백그라운드 → 포그라운드 전환 시 update 강제.
      // 사용자가 staging에 돌아오는 순간 즉시 새 SW 감지.
      const onVisibility = () => {
        if (document.visibilityState === 'visible') {
          r.update().catch((e) => console.error('SW visibility update failed:', e));
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
    },
    onRegisterError(error: any) {
      console.error('SW registration error:', error);
    },
  });

  // 업데이트 감지 시 이전 dismiss 기록 초기화 (새 업데이트면 다시 표시)
  useEffect(() => {
    if (needRefresh) {
      setDismissedUpdateTimestamp(null);
      setUpdateProgress(0);
    }
  }, [needRefresh]);

  // Service Worker controllerchange 이벤트 감지
  // (skipWaiting 후 클라이언트 claim 완료 → 페이지 새로고침)
  useEffect(() => {
    const handleControllerChange = () => {
      setIsUpdating(false);
      setUpdateProgress(100);

      // 200ms 딜레이 후 페이지 새로고침 (UI가 잠깐 표시되도록)
      setTimeout(() => {
        window.location.reload();
      }, 200);
    };

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  const handleUpdateServiceWorker = (reloadPage: boolean) => {
    setIsUpdating(true);
    setUpdateProgress(30);

    // 진행률 애니메이션 (실제 진행률은 trackable하지 않으므로 UI 피드백용)
    const progressInterval = setInterval(() => {
      setUpdateProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 300);

    updateServiceWorker(reloadPage);
  };

  const handleDismissUpdate = () => {
    setDismissedUpdateTimestamp(Date.now());
    setNeedRefresh(false);
  };

  return {
    needRefresh: needRefresh && !dismissedUpdateTimestamp,
    isUpdating,
    updateProgress,
    updateServiceWorker: handleUpdateServiceWorker,
    dismissUpdate: handleDismissUpdate,
  };
}
